import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { QuoteForm } from "../components/QuoteForm";
import { SEO } from "../components/SEO";
import { mockTestimonials, mockProjects, mockServices } from "../mock";
import { ArrowRight, Check, Star } from "lucide-react";
import { localBusinessSchema } from "../utils/structuredData";

export const Homepage = () => {
  const heroImages = [
    "https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/z8odfgv9_image003.png",
    "https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/2wc2g5nn_image006.png",
    "https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/4xysqz7b_image008.png"
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Hudson Knox Outdoor Living | Premium Pergola Installation South East England"
        description="Luxury pergola installation in London, Kent & South East England. Authorised Deponti stockist. Motorised louvered pergolas, glass verandas, and outdoor living solutions. Free quotes available."
        keywords="pergola installation, luxury pergolas, motorised pergolas, louvered pergolas, glass verandas, outdoor living, garden rooms, Deponti pergolas, London pergolas, Kent pergolas, South East England, premium outdoor structures"
        canonicalPath="/"
        structuredData={localBusinessSchema}
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImages[0]})`,
            filter: "brightness(0.4)"
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-2 mb-6">
            <p className="text-white text-sm font-medium tracking-wide">AUTHORISED DEPONTI STOCKIST</p>
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-white mb-6 tracking-tight">
            Luxury Pergola Installation
            <span className="block mt-4 text-3xl md:text-5xl text-neutral-300">in South East England</span>
          </h1>
          <p className="text-xl text-neutral-200 mb-8 max-w-2xl mx-auto">
            Transform your garden into a year-round outdoor living space with premium Deponti aluminium pergolas and modern outdoor structures
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-neutral-900 hover:bg-neutral-100 text-lg px-8 py-6">
                Request Free Quote
              </Button>
            </Link>
            <Link to="/projects">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 text-lg px-8 py-6">
                View Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-light text-neutral-900 mb-6">Authorised Deponti Stockist</h2>
            <p className="text-lg text-neutral-600 leading-relaxed">
              We are an authorised Deponti stockist and specialist installer of premium aluminium pergolas and outdoor structures across South East England. 
              From the Pinela Deluxe motorised louvered system to the Giallo Plus glass veranda, we supply and install premium Dutch-engineered 
              aluminium structures that deliver year-round outdoor living.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-neutral-900 mb-4">Our Services</h2>
            <p className="text-lg text-neutral-600">Premium outdoor living solutions</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockServices.filter(s => s.featured).map((service) => (
              <Card key={service.id} className="border-neutral-200 hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-neutral-900 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <div className="w-6 h-6 bg-white rounded-sm" />
                  </div>
                  <h3 className="text-xl font-medium text-neutral-900 mb-3">{service.title}</h3>
                  <p className="text-neutral-600 mb-4">{service.shortDescription}</p>
                  <Link to={`/services/${service.slug}`} className="inline-flex items-center text-neutral-900 font-medium hover:gap-2 transition-all">
                    Learn More <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-light mb-6">Why Choose a Pergola?</h2>
              <p className="text-neutral-300 text-lg mb-8">
                A luxury pergola transforms your outdoor space into a sophisticated living area that can be enjoyed year-round.
              </p>
              <ul className="space-y-4">
                {[
                  "Year-round outdoor living",
                  "Increased property value",
                  "Weather protection with style",
                  "Customisable designs",
                  "Smart automation options",
                  "Professional installation"
                ].map((benefit, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <Check className="h-5 w-5 text-white flex-shrink-0" />
                    <span className="text-neutral-200">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/2wc2g5nn_image006.png"
                alt="Deponti glass roof veranda with lounge"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-neutral-900 mb-4">Featured Projects</h2>
            <p className="text-lg text-neutral-600">Recent installations across London and Kent</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockProjects.slice(0, 3).map((project) => (
              <div key={project.id} className="group cursor-pointer">
                <div className="relative h-72 rounded-lg overflow-hidden mb-4">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-sm opacity-90">{project.location}</p>
                    <h3 className="text-xl font-medium">{project.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/projects">
              <Button variant="outline" size="lg" className="border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white">
                View All Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-neutral-900 mb-4">What Our Clients Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-neutral-200">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-neutral-900 text-neutral-900" />
                    ))}
                  </div>
                  <p className="text-neutral-600 mb-6 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-medium text-neutral-900">{testimonial.name}</p>
                    <p className="text-sm text-neutral-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-neutral-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-white mb-4">Start Your Project</h2>
            <p className="text-lg text-neutral-400">Get a free quote for your outdoor living space</p>
          </div>
          <QuoteForm />
        </div>
      </section>
    </div>
  );
};
